<template>
    <div ref="host" class="safe-qr-code" aria-label="Two-factor authentication QR code" role="img" />
</template>

<script setup>
/*
 * SUPPORT-708 (F-034): SafeQrCode.
 *
 * The single audited DOM insertion point for TOTP QR SVG. Consumers pass
 * the raw Fortify SVG string via :svg; this component sanitizes it through
 * `sanitizeQrCodeSvg` and writes the result into the host element via a
 * template ref + `.innerHTML =` — deliberately not v-html, so the
 * source-level allowlist guard can keep enforcing "no raw v-html for QR"
 * without needing to allowlist this component.
 *
 * If sanitization returns an empty string (oversized input, non-SVG root,
 * banned element, etc.) the host element renders empty. Callers can gate
 * on the emitted `rendered` event to fall back to a manual-entry code
 * flow.
 */
import { onMounted, ref, watch } from 'vue';
import { sanitizeQrCodeSvg } from '@/utils/safeQrCode';

const props = defineProps({
    svg: {
        type: String,
        default: '',
    },
});

const emit = defineEmits(['rendered']);

const host = ref(null);

function paint(raw) {
    if (!host.value) {
        return;
    }
    const safe = sanitizeQrCodeSvg(raw);
    host.value.innerHTML = safe;
    emit('rendered', safe.length > 0);
}

onMounted(() => paint(props.svg));
watch(
    () => props.svg,
    (next) => paint(next),
);
</script>
